import type Phaser from 'phaser';
import type { BattleEvent, BattleSnapshot, DebugSnapshot } from '../../core';
import type { PresentationCue } from './phaser-battle-adapter';
import { createAttackTrail, cueToCombatEffect } from './combat-effect-model';
import type { CombatVisualEffect } from './combat-effect-model';
import type { StickViewModel } from './stick-figure-model';
import { SkillStatusRenderer } from '../skill/skill-status-renderer';
import { SKILL_COLORS } from '../skill/skill-presentation-model';

export const MAX_TRANSIENT_EFFECTS = 64;

export class CombatEffectLayer {
  private effects: CombatVisualEffect[] = [];
  public readonly statusRenderer: SkillStatusRenderer;

  public constructor(private readonly graphics: Phaser.GameObjects.Graphics) {
    this.statusRenderer = new SkillStatusRenderer();
  }

  public updateEvents(events: readonly BattleEvent[]): void {
    this.statusRenderer.updateEvents(events);
  }

  public stepFrame(): void {
    this.statusRenderer.stepFrame();
  }

  public consume(cues: readonly PresentationCue[], snapshot: BattleSnapshot, nowMs: number): void {
    for (const cue of cues) {
      const effect = cueToCombatEffect(cue, snapshot, nowMs);
      if (effect !== null) this.effects.push(effect);
    }
    if (this.effects.length > MAX_TRANSIENT_EFFECTS) {
      this.effects.splice(0, this.effects.length - MAX_TRANSIENT_EFFECTS);
    }
  }

  public render(snapshot: BattleSnapshot, debug: DebugSnapshot, views: readonly [StickViewModel, StickViewModel], nowMs: number): void {
    this.effects = this.effects.filter(({ expiresAtMs }) => expiresAtMs > nowMs);
    this.graphics.clear();

    // 1. Character State Effects (Trails, Skill Startup/Active Poses)
    snapshot.characters.forEach((character, player) => {
      const view = views[player as 0 | 1];
      const trail = createAttackTrail(view, character.position.x, character.position.y, character.facing);
      if (trail !== null) {
        this.graphics.lineStyle(trail.width, trail.color, 0.55).lineBetween(trail.fromX, trail.y, trail.toX, trail.y);
      }
      this.drawStateEffect(view, character.characterDefinitionId, character.position.x, character.position.y, character.facing, nowMs);
    });

    // 2. Persistent Skill Statuses & Spaghetti Code Floor Traps (Strictly driven by Core state / debug.boxes)
    this.statusRenderer.render(this.graphics, snapshot, debug, views, nowMs);

    // 3. Transient Combat Impacts (Hit sparks, Skill impacts, Guard rings, Super hits)
    for (const effect of this.effects) {
      this.drawTransient(effect, nowMs);
    }

    // 4. Hitstop Subtle Flash
    if (snapshot.hitstopFrames > 0) {
      this.graphics.fillStyle(0xffffff, 0.05 + Math.min(snapshot.hitstopFrames, 10) * 0.01).fillRect(0, 0, 1_280, 720);
    }
  }

  public get activeCount(): number {
    return this.effects.length;
  }

  public get activeKinds(): readonly string[] {
    return Object.freeze(this.effects.map(({ kind }) => kind));
  }

  public dispose(): void {
    this.effects = [];
    this.statusRenderer.clear();
    this.graphics.clear();
    this.graphics.destroy();
  }

  private drawStateEffect(
    view: StickViewModel,
    characterDefinitionId: string,
    x: number,
    y: number,
    facing: 'LEFT' | 'RIGHT',
    nowMs: number,
  ): void {
    const direction = facing === 'RIGHT' ? 1 : -1;
    const isSales = characterDefinitionId === 'sales';

    if (view.pose.state === 'skill1') {
      if (isSales) {
        // Sales Skill 1 (二枚舌ラッシュ): Rapid corporate talk / multi-hit forward momentum
        if (view.pose.attackPhase === 'STARTUP' || view.pose.attackPhase === 'ACTIVE') {
          const yellow = SKILL_COLORS.sales.primary;
          const orange = SKILL_COLORS.sales.secondary;
          this.graphics.lineStyle(3, yellow, 0.8);
          for (let offset = -1; offset <= 1; offset += 1) {
            this.graphics.lineBetween(x + direction * 15, y - 120 + offset * 16, x + direction * 95, y - 120 + offset * 16);
          }
          // Small document / talk fragment
          this.graphics.fillStyle(orange, 0.7);
          this.graphics.fillRect(x + direction * 55, y - 135, 12, 8);
        }
      }
      // Developer Skill 1 is rendered as Counter Window by SkillStatusRenderer
    } else if (view.pose.state === 'skill2') {
      if (isSales) {
        // Sales Skill 2 (無理な納期設定): Schedule box & directional thrust
        const red = SKILL_COLORS.sales.deadlineRed;
        const yellow = SKILL_COLORS.sales.deadlineYellow;
        this.graphics.lineStyle(3.5, red, 0.8).strokeRect(x + direction * 30 - 24, y - 165, 48, 52);
        this.graphics.lineStyle(2, yellow, 0.85).lineBetween(x + direction * 30 - 20, y - 145, x + direction * 30 + 20, y - 145);
      } else {
        // Developer Skill 2 (スパゲッティコード): Terminal deployment / circuit sparks
        const cyan = SKILL_COLORS.developer.primary;
        const purple = SKILL_COLORS.developer.secondary;
        const pulse = 0.7 + 0.3 * Math.sin(nowMs * 0.02);
        this.graphics.lineStyle(2, cyan, pulse).strokeRect(x + direction * 25 - 18, y - 150, 36, 28);
        this.graphics.fillStyle(purple, pulse * 0.8).fillCircle(x + direction * 25, y - 150, 4);
      }
    }
  }

  private drawTransient(effect: CombatVisualEffect, nowMs: number): void {
    const life = Math.max(0.15, Math.min(1, (effect.expiresAtMs - nowMs) / 250));
    const { x, y, size, color } = effect;

    // 1. Sales Super Impact
    if (effect.kind === 'SALES_SUPER_HIT') {
      this.graphics.lineStyle(8, 0xfde047, life);
      this.graphics.strokeCircle(x, y, size * (1.1 - life * 0.1));
      this.graphics.lineStyle(4, 0xffffff, life * 0.9);
      this.graphics.strokeCircle(x, y, size * 0.6);
      this.graphics.lineStyle(5, 0xfacc15, life);
      this.graphics.lineBetween(x - size * 1.3, y, x + size * 1.3, y);
      this.graphics.lineBetween(x - size * 0.9, y - size * 0.6, x + size * 0.9, y + size * 0.6);
      this.graphics.lineBetween(x - size * 0.9, y + size * 0.6, x + size * 0.9, y - size * 0.6);
      this.graphics.fillStyle(0xfde047, life * 0.7);
      this.graphics.fillRect(x + size * 0.5, y - size * 0.7, 14, 10);
      this.graphics.fillRect(x - size * 0.7, y + size * 0.4, 14, 10);
      this.graphics.fillRect(x + size * 0.2, y + size * 0.6, 12, 8);
      return;
    }

    // 2. Developer Super Impact
    if (effect.kind === 'DEV_SUPER_HIT') {
      this.graphics.lineStyle(7, 0x38bdf8, life);
      this.graphics.strokeCircle(x, y, size * 0.85);
      this.graphics.lineStyle(4, 0xf472b6, life * 0.85);
      this.graphics.strokeCircle(x, y, size * 0.5);
      this.graphics.lineStyle(4, 0x38bdf8, life);
      this.graphics.lineBetween(x - size * 0.9, y, x + size * 0.9, y);
      this.graphics.lineBetween(x, y - size * 0.9, x, y + size * 0.9);
      this.graphics.fillStyle(0x38bdf8, life * 0.7);
      this.graphics.fillRect(x - size * 0.6, y - size * 0.5, 12, 12);
      this.graphics.fillStyle(0xf472b6, life * 0.7);
      this.graphics.fillRect(x + size * 0.4, y + size * 0.3, 10, 10);
      return;
    }

    // 3. Sales Skill 1 Hits (二枚舌ラッシュ)
    if (effect.kind === 'SALES_SKILL1_HIT') {
      // Small multi-hit pulse with forward orange/yellow lines
      this.graphics.lineStyle(4, 0xf97316, life);
      this.graphics.strokeCircle(x, y, size * 0.75);
      this.graphics.lineStyle(3, 0xfde047, life);
      this.graphics.lineBetween(x - size * 0.8, y - size * 0.3, x + size * 0.8, y + size * 0.3);
      this.graphics.lineBetween(x - size * 0.8, y + size * 0.3, x + size * 0.8, y - size * 0.3);
      this.graphics.fillStyle(0xfde047, life * 0.8);
      this.graphics.fillRect(x + size * 0.2, y - size * 0.5, 8, 6);
      return;
    }

    if (effect.kind === 'SALES_SKILL1_FINISH') {
      // 3rd hit finisher: slightly stronger forward pulse + document fragments
      this.graphics.lineStyle(5.5, 0xfde047, life);
      this.graphics.strokeCircle(x, y, size * 0.85);
      this.graphics.lineStyle(3.5, 0xf97316, life);
      this.graphics.strokeCircle(x, y, size * 0.5);
      this.graphics.lineStyle(4, 0xffffff, life);
      this.graphics.lineBetween(x - size * 1.1, y, x + size * 1.1, y);
      this.graphics.lineBetween(x - size * 0.7, y - size * 0.7, x + size * 0.7, y + size * 0.7);
      this.graphics.fillStyle(0xfde047, life * 0.75);
      this.graphics.fillRect(x - size * 0.5, y + size * 0.3, 10, 8);
      this.graphics.fillRect(x + size * 0.4, y - size * 0.4, 10, 8);
      return;
    }

    // 4. Sales Skill 2 Hit (無理な納期設定 - Instant TIME_PENALTY deduction impact)
    if (effect.kind === 'SALES_SKILL2_HIT') {
      // Clock and deadline calendar burst with red/yellow accents
      this.graphics.lineStyle(5, 0xef4444, life);
      this.graphics.strokeCircle(x, y, size * 0.85);
      // Clock hands pointing urgently
      this.graphics.lineStyle(3, 0xfacc15, life);
      this.graphics.lineBetween(x, y, x, y - size * 0.45);
      this.graphics.lineBetween(x, y, x + size * 0.35, y + size * 0.15);
      // Schedule box motif
      this.graphics.lineStyle(2.5, 0xfacc15, life * 0.85);
      this.graphics.strokeRect(x - size * 0.45, y - size * 0.45, size * 0.9, size * 0.9);
      // Sharp horizontal deadline slash
      this.graphics.lineStyle(4, 0xef4444, life);
      this.graphics.lineBetween(x - size * 1.1, y, x + size * 1.1, y);
      return;
    }

    // 5. Developer Skill 1 Counter Hit (仕様変更カウンター反撃)
    if (effect.kind === 'DEV_COUNTER_HIT') {
      // Sharp cyan/white flash and reversal arc
      this.graphics.lineStyle(6, 0x38bdf8, life);
      this.graphics.strokeCircle(x, y, size * 0.85);
      this.graphics.lineStyle(3.5, 0xffffff, life);
      this.graphics.strokeCircle(x, y, size * 0.45);
      this.graphics.lineStyle(4.5, 0x38bdf8, life);
      // Sharp reversal slash
      this.graphics.lineBetween(x - size * 1.1, y + size * 0.8, x + size * 1.1, y - size * 0.8);
      this.graphics.lineBetween(x - size * 0.5, y - size * 0.9, x + size * 0.5, y + size * 0.9);
      return;
    }

    // 6. Developer Skill 2 Trap Hit (スパゲッティコード Trap)
    if (effect.kind === 'DEV_TRAP_HIT') {
      // Tangled purple/cyan glitch node burst
      this.graphics.lineStyle(5, 0xa855f7, life);
      this.graphics.strokeCircle(x, y, size * 0.8);
      this.graphics.lineStyle(3, 0x38bdf8, life);
      this.graphics.strokeRect(x - size * 0.5, y - size * 0.4, size, size * 0.8);
      this.graphics.lineStyle(3, 0x38bdf8, life);
      this.graphics.lineBetween(x - size * 0.7, y + size * 0.4, x + size * 0.7, y - size * 0.4);
      return;
    }

    // Standard combat effects
    this.graphics.lineStyle(effect.kind === 'HEAVY_HIT' ? 9 : 6, color, life);
    if (effect.kind === 'GUARD_RING') {
      this.graphics.strokeCircle(x, y, size).strokeCircle(x, y, size * 0.65);
      return;
    }
    if (effect.kind === 'SUPER_FLASH') {
      this.graphics.fillStyle(color, life * 0.12).fillRect(0, 0, 1_280, 720).strokeCircle(x, y, size);
      return;
    }
    if (effect.kind === 'COUNTER_FLASH') {
      this.graphics.lineBetween(x - size, y + size, x + size, y - size).lineBetween(x - size * 0.4, y + size, x + size, y - size * 0.4);
      return;
    }
    if (effect.kind === 'KO_BURST') {
      this.graphics.strokeCircle(x, y, size).lineBetween(x - size, y, x + size, y).lineBetween(x, y - size, x, y + size);
      return;
    }
    this.graphics
      .lineBetween(x - size, y, x + size, y)
      .lineBetween(x, y - size, x, y + size)
      .lineBetween(x - size * 0.7, y - size * 0.7, x + size * 0.7, y + size * 0.7)
      .lineBetween(x - size * 0.7, y + size * 0.7, x + size * 0.7, y - size * 0.7);
  }
}
